import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { overtimeRecordsService, workedSaturdaysService, absencesService, recurringBonusesService, extraAdvancesService, expensesService } from '../services/api';
import { Clock, CheckCircle, AlertCircle, ChevronLeft, ChevronRight, Calendar, AlertTriangle, DollarSign, CreditCard } from 'lucide-react';
import toast from 'react-hot-toast';

const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return new Date(dateStr.getFullYear(), dateStr.getMonth(), dateStr.getDate());

  // Handle date strings like "2026-08-31" or "2026-08-31T00:00:00.000Z"
  if (dateStr.includes('T') || dateStr.includes('-')) {
    const datePart = dateStr.split('T')[0].split(' ')[0];
    const [y, m, d] = datePart.split('-').map(Number);
    if (!isNaN(y) && !isNaN(m) && !isNaN(d) && m >= 1 && m <= 12 && d >= 1 && d <= 31) {
      const date = new Date(Number(y), Number(m) - 1, Number(d));
      date.setHours(12, 0, 0, 0);
      return date;
    }
  }

  const timestamp = Date.parse(dateStr);
  if (!isNaN(timestamp)) {
    const d = new Date(timestamp);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  return null;
};

const formatDateLocal = (dateStr) => {
  if (!dateStr) return '';
  const date = parseLocalDate(dateStr);
  if (!date) return '---';
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const MyTimeRecords = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const today = new Date();

  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1);
  const [activeTab, setActiveTab] = useState('overtime');

  const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

  const changeMonth = (delta) => {
    let newMonth = selectedMonth + delta;
    let newYear = selectedYear;
    if (newMonth < 1) { newMonth = 12; newYear--; }
    if (newMonth > 12) { newMonth = 1; newYear++; }
    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
  };

  const goToToday = () => {
    setSelectedMonth(today.getMonth() + 1);
    setSelectedYear(today.getFullYear());
  };

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">Meus Registros</h1>
          <p className="text-sm md:text-base text-gray-600">Visualize seus registros do mês</p>
        </div>
      </div>

      <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between gap-2">
          <button
            onClick={() => changeMonth(-1)}
            aria-label="Mês anterior"
            className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center hover:bg-gray-100 active:bg-gray-200 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-gray-600" />
          </button>

          <div className="flex-1 text-center">
            <select
              value={`${selectedYear}-${selectedMonth}`}
              onChange={(e) => {
                const [y, m] = e.target.value.split('-').map(Number);
                setSelectedYear(y);
                setSelectedMonth(m);
              }}
              className="text-base md:text-lg font-semibold text-gray-900 bg-transparent border-0 cursor-pointer text-center appearance-none focus:outline-none"
            >
              {Array.from({ length: 12 }, (_, i) => {
                const m = i + 1;
                const isCurrent = m === today.getMonth() + 1 && selectedYear === today.getFullYear();
                const label = `${monthNames[m - 1]} ${selectedYear}`;
                return (
                  <option key={m} value={`${selectedYear}-${m}`} disabled={!isCurrent && m > today.getMonth() + 1 && selectedYear >= today.getFullYear()}>
                    {label}
                  </option>
                );
              })}
            </select>
            <p className="text-sm text-gray-600 mt-0.5">{monthNames[selectedMonth - 1]} {selectedYear}</p>
          </div>

          <button
            onClick={() => changeMonth(1)}
            aria-label="Próximo mês"
            className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center hover:bg-gray-100 active:bg-gray-200 rounded-lg transition-colors"
          >
            <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-gray-600" />
          </button>
        </div>

        <div className="flex justify-center gap-2 mt-3">
          <button
            onClick={goToToday}
            className="text-xs md:text-sm font-medium text-blue-600 hover:text-blue-700 px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors"
          >
            Hoje
          </button>
        </div>
      </section>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex border-b border-gray-200 overflow-x-auto scrollbar-hide snap-x snap-mandatory">
          <button
            onClick={() => setActiveTab('overtime')}
            className={`snap-start min-w-[72px] sm:min-w-[110px] px-2 sm:px-4 py-2 sm:py-3 text-[12px] sm:text-sm font-medium flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-2 transition-colors whitespace-nowrap border-b-2 ${
              activeTab === 'overtime'
                ? 'text-blue-600 bg-blue-50 border-blue-600'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-transparent'
            }`}
          >
            <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Horas Extras</span>
            <span className="sm:hidden">HE</span>
          </button>
          <button
            onClick={() => setActiveTab('saturdays')}
            className={`snap-start min-w-[72px] sm:min-w-[110px] px-2 sm:px-4 py-2 sm:py-3 text-[12px] sm:text-sm font-medium flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-2 transition-colors whitespace-nowrap border-b-2 ${
              activeTab === 'saturdays'
                ? 'text-blue-600 bg-blue-50 border-blue-600'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-transparent'
            }`}
          >
            <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Sábados</span>
            <span className="sm:hidden">Sáb</span>
          </button>
          <button
            onClick={() => setActiveTab('absences')}
            className={`snap-start min-w-[72px] sm:min-w-[110px] px-2 sm:px-4 py-2 sm:py-3 text-[12px] sm:text-sm font-medium flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-2 transition-colors whitespace-nowrap border-b-2 ${
              activeTab === 'absences'
                ? 'text-blue-600 bg-blue-50 border-blue-600'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-transparent'
            }`}
          >
            <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Divergências</span>
            <span className="sm:hidden">Div</span>
          </button>
          <button
            onClick={() => setActiveTab('bonuses')}
            className={`snap-start min-w-[72px] sm:min-w-[110px] px-2 sm:px-4 py-2 sm:py-3 text-[12px] sm:text-sm font-medium flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-2 transition-colors whitespace-nowrap border-b-2 ${
              activeTab === 'bonuses'
                ? 'text-blue-600 bg-blue-50 border-blue-600'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-transparent'
            }`}
          >
            <DollarSign className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Bônus mensal</span>
            <span className="sm:hidden">Bônus</span>
          </button>
          <button
            onClick={() => setActiveTab('extras')}
            className={`snap-start min-w-[72px] sm:min-w-[110px] px-2 sm:px-4 py-2 sm:py-3 text-[12px] sm:text-sm font-medium flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-2 transition-colors whitespace-nowrap border-b-2 ${
              activeTab === 'extras'
                ? 'text-blue-600 bg-blue-50 border-blue-600'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-transparent'
            }`}
          >
            <DollarSign className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Extras</span>
            <span className="sm:hidden">Ext</span>
          </button>
          <button
            onClick={() => setActiveTab('emprestimos')}
            className={`snap-start min-w-[72px] sm:min-w-[110px] px-2 sm:px-4 py-2 sm:py-3 text-[12px] sm:text-sm font-medium flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-2 transition-colors whitespace-nowrap border-b-2 ${
              activeTab === 'emprestimos'
                ? 'text-blue-600 bg-blue-50 border-blue-600'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 border-transparent'
            }`}
          >
            <CreditCard className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Vales/Empréstimos</span>
            <span className="sm:hidden">V/E</span>
          </button>
        </div>

        <div className="p-4">
          {activeTab === 'overtime' && (
            <OvertimeTab
              user={user}
              selectedYear={selectedYear}
              selectedMonth={selectedMonth}
              isCurrentMonth={selectedMonth === today.getMonth() + 1 && selectedYear === today.getFullYear()}
              today={today}
            />
          )}
          {activeTab === 'saturdays' && (
            <SaturdaysTab
              user={user}
              selectedYear={selectedYear}
              selectedMonth={selectedMonth}
              isCurrentMonth={selectedMonth === today.getMonth() + 1 && selectedYear === today.getFullYear()}
              today={today}
            />
          )}
          {activeTab === 'absences' && (
            <AbsencesTab
              user={user}
              selectedYear={selectedYear}
              selectedMonth={selectedMonth}
            />
          )}
          {activeTab === 'bonuses' && (
            <BonusesTab
              user={user}
              selectedYear={selectedYear}
              selectedMonth={selectedMonth}
            />
          )}
          {activeTab === 'extras' && (
            <ExtrasTab
              user={user}
              selectedYear={selectedYear}
              selectedMonth={selectedMonth}
            />
          )}
          {activeTab === 'emprestimos' && (
            <EmprestimosTab
              user={user}
              selectedYear={selectedYear}
              selectedMonth={selectedMonth}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const monthNamesFull = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const OvertimeTab = ({ user, selectedYear, selectedMonth, isCurrentMonth, today }) => {
  const { data: overtimeData, isLoading: loadingRecords } = useQuery({
    queryKey: ['my-overtime-records', user?.id, selectedYear, selectedMonth],
    queryFn: () => overtimeRecordsService.getOvertimeRecords({
      userId: user.id,
      year: selectedYear,
      month: selectedMonth
    }),
    enabled: !!user?.id,
    staleTime: 30000,
  });

  const { data: reportData, isLoading: loadingReport } = useQuery({
    queryKey: ['my-overtime-report', user?.id, selectedYear, selectedMonth],
    queryFn: () => overtimeRecordsService.getMonthlyReport({
      userId: user.id,
      year: selectedYear,
      month: selectedMonth
    }),
    enabled: !!user?.id,
    staleTime: 30000,
  });

  const records = useMemo(() => {
    if (!overtimeData?.data?.data) return [];
    if (Array.isArray(overtimeData.data.data)) return overtimeData.data.data;
    if (Array.isArray(overtimeData.data)) return overtimeData.data;
    return [];
  }, [overtimeData]);

  const report = reportData?.data?.data;

  const totalMinutes = useMemo(() => {
    return records.reduce((sum, r) => sum + (r.minutes || 0), 0);
  }, [records]);

  const formatTime = (minutes) => {
    if (!minutes || minutes === 0) return '0h';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h === 0) return `${m}min`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}min`;
  };

const getDaysInMonth = (year, month) => new Date(year, month, 0).getDate();
  const getFirstDayOfMonth = (year, month) => new Date(year, month - 1, 1).getDay();

  const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
  const firstDay = getFirstDayOfMonth(selectedYear, selectedMonth);

  const recordMap = useMemo(() => {
    const map = {};
    records.forEach(r => {
      if (!r.date) return;
      const date = parseLocalDate(r.date);
      if (!date) return;
      const key = `${date.getDate()}`;
      map[key] = r.minutes || 0;
    });
    return map;
  }, [records]);

  const isToday = (day) => isCurrentMonth && day === today.getDate();

  const isApproved = report?.reportStatus === 'APPROVED';
  const isPending = report?.reportStatus === 'PENDING_APPROVAL' || report?.reportStatus === 'PENDING';
  const pendingApprovers = report?.pendingApprovers || [];

  const isLoading = loadingRecords || loadingReport;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <section className="bg-gray-50 rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <Clock className="w-4 h-4 text-blue-600" />
            </div>
            <p className="text-sm text-gray-600">Total registrado</p>
          </div>
          <p className="text-2xl md:text-3xl font-bold text-gray-900">{formatTime(totalMinutes)}</p>
          <p className="text-sm text-gray-500 mt-1">{records.length} dia(s) com horas</p>
        </section>

        <section className={`rounded-lg border p-4 ${isApproved ? 'bg-green-50 border-green-200' : isPending ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isApproved ? 'bg-green-100' : isPending ? 'bg-amber-100' : 'bg-gray-100'}`}>
              {isApproved ? (
                <CheckCircle className="w-4 h-4 text-green-600" />
              ) : isPending ? (
                <AlertCircle className="w-4 h-4 text-amber-600" />
              ) : (
                <Clock className="w-4 h-4 text-gray-400" />
              )}
            </div>
            <p className="text-sm text-gray-600">Status do mês</p>
          </div>
          <p className={`text-lg md:text-xl font-bold ${isApproved ? 'text-green-700' : isPending ? 'text-amber-700' : 'text-gray-500'}`}>
            {isApproved ? 'Aprovado' : isPending ? 'Pendente' : 'Sem registro'}
          </p>
          {pendingApprovers.length > 0 && (
            <p className="text-xs md:text-sm text-amber-600 mt-1">
              Aguardando: {pendingApprovers.map(a => a.name?.split(' ')[0]).join(', ')}
            </p>
          )}
        </section>
      </div>

      <section className="bg-gray-50 rounded-lg border border-gray-200 p-4">
        <p className="text-sm font-medium text-gray-600 mb-3">Calendário</p>

        <div className="grid grid-cols-7 gap-1 mb-1">
          {weekDays.map(day => (
            <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: firstDay }, (_, i) => (
            <div key={`empty-${i}`} className="min-h-[48px]" />
          ))}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const day = i + 1;
            const minutes = recordMap[day] || 0;
            const hasMinutes = minutes > 0;
            const isTodayCell = isToday(day);
            return (
              <div
                key={day}
                className={`min-h-[48px] flex flex-col items-center justify-center rounded-lg text-sm ${
                  isTodayCell
                    ? 'bg-blue-500 text-white font-semibold'
                    : hasMinutes
                    ? 'bg-blue-100 text-blue-700 font-medium'
                    : 'text-gray-400'
                }`}
              >
                <span>{day}</span>
                {hasMinutes && !isTodayCell && (
                  <span className="text-xs text-blue-500">{minutes}m</span>
                )}
                {isTodayCell && (
                  <span className="text-xs text-blue-200">hoje</span>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex items-center gap-4 mt-4 pt-3 border-t border-gray-200">
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded bg-blue-100 border border-blue-200"></div>
            <span className="text-xs text-gray-600">Com horas extras</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded bg-blue-500"></div>
            <span className="text-xs text-gray-600">Hoje</span>
          </div>
        </div>
      </section>

      {records.length > 0 && (
        <section className="bg-gray-50 rounded-lg border border-gray-200 p-4">
          <p className="text-sm font-medium text-gray-600 mb-3">Registros do mês</p>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {records
              .filter(r => r.date)
              .sort((a, b) => {
                const dateA = parseLocalDate(a.date);
                const dateB = parseLocalDate(b.date);
                if (!dateA || !dateB) return 0;
                return dateA - dateB;
              })
              .map((record) => {
                const date = parseLocalDate(record.date);
                const dayIndex = date ? date.getDay() : 0;
                const dayName = weekDays[dayIndex] || '---';
                return (
                  <div key={record.id || record.date} className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <span className="text-sm font-bold text-blue-600">{dayName.slice(0, 3)}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{formatDateLocal(record.date)}</p>
                        <p className="text-xs text-gray-500">{dayName}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-blue-600">{record.minutes}m</p>
                      <p className="text-xs text-gray-500">{formatTime(record.minutes)}</p>
                    </div>
                  </div>
                );
              })}
          </div>
        </section>
      )}

      {records.length === 0 && !isLoading && (
        <section className="bg-gray-50 rounded-lg border border-gray-200 p-6 text-center">
          <Clock className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm font-medium text-gray-900 mb-1">Nenhuma hora extra registrada</p>
          <p className="text-xs text-gray-500">Não há registros de horas extras para este mês.</p>
        </section>
      )}

      {isLoading && (
        <section className="bg-gray-50 rounded-lg border border-gray-200 p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-blue-600 mx-auto mb-2"></div>
          <p className="text-sm text-gray-500">Carregando...</p>
        </section>
      )}
    </div>
  );
};

const SaturdaysTab = ({ user, selectedYear, selectedMonth, isCurrentMonth, today }) => {
  const queryClient = useQueryClient();

  const { data: saturdaysData, isLoading: loadingSaturdays } = useQuery({
    queryKey: ['my-worked-saturdays', user?.id, selectedYear, selectedMonth],
    queryFn: () => workedSaturdaysService.getWorkedSaturdays({
      userId: user.id,
      year: selectedYear,
      month: selectedMonth
    }),
    enabled: !!user?.id,
    staleTime: 30000,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ date }) => workedSaturdaysService.toggleWorkedSaturday(user.id, { date }),
    onSuccess: () => {
      queryClient.invalidateQueries(['my-worked-saturdays']);
      toast.success('Sábado atualizado com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao atualizar sábado');
    }
  });

  const saturdays = useMemo(() => {
    if (!saturdaysData?.data?.data) return [];
    if (Array.isArray(saturdaysData.data.data)) return saturdaysData.data.data;
    if (Array.isArray(saturdaysData.data)) return saturdaysData.data;
    return [];
  }, [saturdaysData]);

  const workedSaturdays = useMemo(() => {
    return saturdays.filter(s => s.worked).map(s => s.date);
  }, [saturdays]);

  const saturdayDates = useMemo(() => {
    const dates = [];
    const firstDay = new Date(selectedYear, selectedMonth - 1, 1);
    const lastDay = new Date(selectedYear, selectedMonth, 0);

    for (let d = new Date(firstDay); d <= lastDay; d.setDate(d.getDate() + 1)) {
      if (d.getDay() === 6) {
        dates.push(new Date(d));
      }
    }
    return dates;
  }, [selectedYear, selectedMonth]);

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  const isToday = (date) => {
    const d = new Date(date);
    return isCurrentMonth && d.getDate() === today.getDate();
  };

  const isWorked = (date) => {
    const d = new Date(date);
    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return workedSaturdays.includes(dateStr);
  };

  const getDayName = (date) => {
    return weekDays[new Date(date).getDay()];
  };

  const getMonthProgress = () => {
    if (saturdayDates.length === 0) return { worked: 0, total: 0 };
    const worked = saturdayDates.filter(d => isWorked(d)).length;
    return { worked, total: saturdayDates.length };
  };

  const progress = getMonthProgress();

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <section className="bg-gray-50 rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-4 h-4 text-green-600" />
            </div>
            <p className="text-sm text-gray-600">Sábados trabajados</p>
          </div>
          <p className="text-2xl md:text-3xl font-bold text-gray-900">{progress.worked}</p>
          <p className="text-sm text-gray-500 mt-1">de {progress.total} sábados do mês</p>
        </section>

        <section className="bg-gray-50 rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <Calendar className="w-4 h-4 text-blue-600" />
            </div>
            <p className="text-sm text-gray-600">Progresso mensal</p>
          </div>
          <div className="mt-2">
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div
                className="bg-green-500 h-2.5 rounded-full transition-all"
                style={{ width: `${progress.total > 0 ? (progress.worked / progress.total) * 100 : 0}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {progress.total > 0 ? Math.round((progress.worked / progress.total) * 100) : 0}% completo
            </p>
          </div>
        </section>
      </div>

      <section className="bg-gray-50 rounded-lg border border-gray-200 p-4">
        <p className="text-sm font-medium text-gray-600 mb-3">Calendário de sábados</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          {saturdayDates.length === 0 ? (
            <div className="col-span-full text-center py-4">
              <p className="text-sm text-gray-500">Não há sábados neste mês.</p>
            </div>
          ) : (
            saturdayDates.map((date) => {
              const worked = isWorked(date);
              const todaySat = isToday(date);
              const dayName = getDayName(date);

              return (
                <button
                  key={date.toISOString()}
                  onClick={() => toggleMutation.mutate({ date: formatDateLocal(date) })}
                  disabled={toggleMutation.isPending}
                  className={`p-3 rounded-lg border-2 flex items-center justify-between transition-all ${
                    worked
                      ? 'bg-green-50 border-green-300 hover:border-green-400'
                      : 'bg-white border-gray-200 hover:border-gray-300'
                  } ${todaySat ? 'ring-2 ring-blue-400 ring-offset-1' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      worked ? 'bg-green-100' : 'bg-gray-100'
                    }`}>
                      <span className={`text-xs font-bold ${worked ? 'text-green-600' : 'text-gray-500'}`}>
                        {dayName.slice(0, 3)}
                      </span>
                    </div>
                    <div className="text-left">
                      <p className={`text-sm font-medium ${worked ? 'text-green-700' : 'text-gray-700'}`}>
                        {formatDateLocal(date)}
                      </p>
                      <p className="text-xs text-gray-500">{dayName}</p>
                    </div>
                  </div>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                    worked ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'
                  }`}>
                    {worked ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>

        <div className="flex items-center gap-4 mt-4 pt-3 border-t border-gray-200">
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded bg-green-100 border border-green-200"></div>
            <span className="text-xs text-gray-600">Trabalhado</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded bg-white border border-gray-200"></div>
            <span className="text-xs text-gray-600">Não trabalhado</span>
          </div>
        </div>
      </section>

      {loadingSaturdays && (
        <section className="bg-gray-50 rounded-lg border border-gray-200 p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-blue-600 mx-auto mb-2"></div>
          <p className="text-sm text-gray-500">Carregando...</p>
        </section>
      )}
    </div>
  );
};

const AbsencesTab = ({ user, selectedYear, selectedMonth }) => {
  const { data: absencesData, isLoading } = useQuery({
    queryKey: ['my-absences', user?.id, selectedYear, selectedMonth],
    queryFn: () => absencesService.getAbsences({
      userId: user.id,
      year: selectedYear,
      month: selectedMonth
    }),
    enabled: !!user?.id,
    staleTime: 30000,
  });

  const absences = useMemo(() => {
    if (!absencesData?.data?.data) return [];
    if (Array.isArray(absencesData.data.data)) return absencesData.data.data;
    if (Array.isArray(absencesData.data)) return absencesData.data;
    return [];
  }, [absencesData]);

  const getTypeLabel = (type) => {
    const labels = {
      'FULL_DAY': 'Dia Inteiro',
      'HOURS': 'Horas',
      'DAYS': 'Dias'
    };
    return labels[type] || type;
  };

  const totalUnjustified = useMemo(() => {
    return absences.filter(a => !a.justified).length;
  }, [absences]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <section className="bg-gray-50 rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 text-red-600" />
            </div>
            <p className="text-sm text-gray-600">Total de divergências</p>
          </div>
          <p className="text-2xl md:text-3xl font-bold text-gray-900">{absences.length}</p>
          <p className="text-sm text-gray-500 mt-1">registros este mês</p>
        </section>

        <section className={`rounded-lg border p-4 ${totalUnjustified > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${totalUnjustified > 0 ? 'bg-amber-100' : 'bg-green-100'}`}>
              {totalUnjustified > 0 ? (
                <AlertCircle className="w-4 h-4 text-amber-600" />
              ) : (
                <CheckCircle className="w-4 h-4 text-green-600" />
              )}
            </div>
            <p className="text-sm text-gray-600">Pendências</p>
          </div>
          <p className={`text-lg md:text-xl font-bold ${totalUnjustified > 0 ? 'text-amber-700' : 'text-green-700'}`}>
            {totalUnjustified > 0 ? `${totalUnjustified} sem justificativa` : 'Todas justificadas'}
          </p>
        </section>
      </div>

      {absences.length > 0 && (
        <section className="bg-gray-50 rounded-lg border border-gray-200 p-4">
          <p className="text-sm font-medium text-gray-600 mb-3">Registros do mês</p>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {absences.map((absence) => {
              const date = new Date(absence.date);
              const dayName = weekDays[date.getDay()];
              return (
                <div key={absence.id} className="flex items-center justify-between p-3 bg-white rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${absence.justified ? 'bg-green-100' : 'bg-red-100'}`}>
                      <AlertTriangle className={`w-5 h-5 ${absence.justified ? 'text-green-600' : 'text-red-600'}`} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{formatDateLocal(absence.date)}</p>
                      <p className="text-xs text-gray-500">{dayName} - {getTypeLabel(absence.type)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${absence.justified ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {absence.justified ? 'Abonado' : 'Pendente'}
                    </span>
                    {absence.hours && (
                      <p className="text-xs text-gray-500 mt-1">{absence.hours}h</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {absences.length === 0 && !isLoading && (
        <section className="bg-gray-50 rounded-lg border border-gray-200 p-6 text-center">
          <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
          <p className="text-sm font-medium text-gray-900 mb-1">Nenhuma divergência registrada</p>
          <p className="text-xs text-gray-500">Você não tem divergências registradas neste mês.</p>
        </section>
      )}

      {isLoading && (
        <section className="bg-gray-50 rounded-lg border border-gray-200 p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-blue-600 mx-auto mb-2"></div>
          <p className="text-sm text-gray-500">Carregando...</p>
        </section>
      )}
    </div>
  );
};

const BonusesTab = ({ user, selectedYear, selectedMonth }) => {
  const { data: bonusesData, isLoading } = useQuery({
    queryKey: ['my-bonuses', user?.id, selectedYear, selectedMonth],
    queryFn: () => recurringBonusesService.getMyRecurringBonuses(),
    enabled: !!user?.id,
    staleTime: 30000,
  });

  const bonuses = useMemo(() => {
    if (!bonusesData?.data?.data) return [];
    if (Array.isArray(bonusesData.data.data)) return bonusesData.data.data;
    if (Array.isArray(bonusesData.data)) return bonusesData.data;
    return [];
  }, [bonusesData]);

  const activeBonuses = useMemo(() => {
    return bonuses.filter(b => {
      if (!b.is_active) return false;
      const startOk = (b.start_year < selectedYear) ||
        (b.start_year === selectedYear && b.start_month <= selectedMonth);
      const endOk = (!b.end_year || !b.end_month) ||
        (b.end_year > selectedYear) ||
        (b.end_year === selectedYear && b.end_month >= selectedMonth);
      return startOk && endOk;
    });
  }, [bonuses, selectedYear, selectedMonth]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Indeterminado';
    const date = parseLocalDate(dateStr);
    return `${monthNamesFull[date.getMonth()]} ${date.getFullYear()}`;
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <section className="bg-gray-50 rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
              <DollarSign className="w-4 h-4 text-green-600" />
            </div>
            <p className="text-sm text-gray-600">Bônus ativos</p>
          </div>
          <p className="text-2xl md:text-3xl font-bold text-gray-900">{activeBonuses.length}</p>
          <p className="text-sm text-gray-500 mt-1">bônus recorrentes</p>
        </section>

        <section className="bg-gray-50 rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <DollarSign className="w-4 h-4 text-blue-600" />
            </div>
            <p className="text-sm text-gray-600">Valor total mensal</p>
          </div>
          <p className="text-lg md:text-xl font-bold text-gray-900">
            {formatCurrency(activeBonuses.reduce((sum, b) => sum + (b.amount || 0), 0))}
          </p>
          <p className="text-sm text-gray-500 mt-1">por mês</p>
        </section>
      </div>

      {bonuses.length > 0 && (
        <section className="bg-gray-50 rounded-lg border border-gray-200 p-4">
          <p className="text-sm font-medium text-gray-600 mb-3">Meus bonus mensais</p>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {bonuses.map((bonus) => (
              <div key={bonus.id} className="flex items-center justify-between p-3 bg-white rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${bonus.is_active ? 'bg-green-100' : 'bg-gray-100'}`}>
                    <DollarSign className={`w-5 h-5 ${bonus.is_active ? 'text-green-600' : 'text-gray-400'}`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{bonus.description || 'Bônus recorrente'}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-green-600">{formatCurrency(bonus.amount)}</p>
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${bonus.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                    {bonus.is_active ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {bonuses.length === 0 && !isLoading && (
        <section className="bg-gray-50 rounded-lg border border-gray-200 p-6 text-center">
          <DollarSign className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm font-medium text-gray-900 mb-1">Nenhum bônus registrado</p>
          <p className="text-xs text-gray-500">Você não tem bônus recorrentes cadastrados.</p>
        </section>
      )}

      {isLoading && (
        <section className="bg-gray-50 rounded-lg border border-gray-200 p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-blue-600 mx-auto mb-2"></div>
          <p className="text-sm text-gray-500">Carregando...</p>
        </section>
      )}
    </div>
  );
};

const ExtrasTab = ({ user, selectedYear, selectedMonth }) => {
  const { data: extrasData, isLoading } = useQuery({
    queryKey: ['my-extras', user?.id, selectedYear, selectedMonth],
    queryFn: () => extraAdvancesService.getMyExtraAdvances(),
    enabled: !!user?.id,
    staleTime: 30000,
  });

  const extras = useMemo(() => {
    if (!extrasData?.data?.data) return [];
    if (Array.isArray(extrasData.data.data)) return extrasData.data.data;
    if (Array.isArray(extrasData.data)) return extrasData.data;
    return [];
  }, [extrasData]);

  const filteredExtras = useMemo(() => {
    return extras.filter(e => {
      if (parseInt(e.deduction_year) !== selectedYear) return false;
      if (parseInt(e.deduction_month) !== selectedMonth) return false;
      const desc = (e.description || '').toLowerCase();
      if (desc.includes('bônus referente a abono')) return false;
      return true;
    });
  }, [extras, selectedYear, selectedMonth]);

  const totalExtras = useMemo(() => {
    return filteredExtras.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
  }, [filteredExtras]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '---';
    if (dateStr instanceof Date) {
      return dateStr.toLocaleDateString('pt-BR');
    }
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      const datePart = dateStr.split('T')[0];
      const [y, m, d] = datePart.split('-');
      return `${d}/${m}/${y}`;
    }
    return date.toLocaleDateString('pt-BR');
  };

  const formatMonthYear = (month, year) => {
    if (!month || !year) return '---';
    return `${monthNamesFull[month - 1]} ${year}`;
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <section className="bg-gray-50 rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
              <DollarSign className="w-4 h-4 text-orange-600" />
            </div>
            <p className="text-sm text-gray-600">Total de extras</p>
          </div>
          <p className="text-2xl md:text-3xl font-bold text-gray-900">{filteredExtras.length}</p>
          <p className="text-sm text-gray-500 mt-1">registro(s)</p>
        </section>
      </div>

      {filteredExtras.length > 0 && (
        <section className="bg-gray-50 rounded-lg border border-gray-200 p-4">
          <p className="text-sm font-medium text-gray-600 mb-3">Meus extras</p>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {filteredExtras.map((extra) => (
              <div key={extra.id} className="flex items-center justify-between p-3 bg-white rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-green-100">
                    <DollarSign className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{extra.description || 'Extra'}</p>
                    <p className="text-xs text-gray-500">
                      {formatMonthYear(extra.deduction_month, extra.deduction_year)} • {formatDate(extra.request_date)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {filteredExtras.length === 0 && !isLoading && (
        <section className="bg-gray-50 rounded-lg border border-gray-200 p-6 text-center">
          <DollarSign className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm font-medium text-gray-900 mb-1">Nenhum extra registrado</p>
          <p className="text-xs text-gray-500">Você não tem extras cadastrados.</p>
        </section>
      )}

      {isLoading && (
        <section className="bg-gray-50 rounded-lg border border-gray-200 p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-blue-600 mx-auto mb-2"></div>
          <p className="text-sm text-gray-500">Carregando...</p>
        </section>
      )}
    </div>
  );
};

const EmprestimosTab = ({ user, selectedYear, selectedMonth }) => {
  const { data: expensesData, isLoading } = useQuery({
    queryKey: ['my-emprestimos', user?.id, selectedYear, selectedMonth],
    queryFn: () => expensesService.getMyExpenses(),
    enabled: !!user?.id,
    staleTime: 30000,
  });

  const expenses = useMemo(() => {
    if (!expensesData?.data?.data) return [];
    if (Array.isArray(expensesData.data.data)) return expensesData.data.data;
    if (Array.isArray(expensesData.data)) return expensesData.data;
    return [];
  }, [expensesData]);

  const filteredExpenses = useMemo(() => {
    return expenses.filter(e => {
      if (e.status === 'CANCELLED') return false;

      const startYear = parseInt(e.start_year);
      const startMonth = parseInt(e.start_month);
      const installmentsCount = parseInt(e.installments_count) || 1;

      const endMonth = startMonth + installmentsCount - 1;
      const endYear = startMonth + installmentsCount - 1 > 12
        ? startYear + Math.floor((startMonth + installmentsCount - 2) / 12)
        : startYear;

      if (endYear < selectedYear) return false;
      if (endYear === selectedYear && endMonth < selectedMonth) return false;
      if (startYear > selectedYear) return false;
      if (startYear === selectedYear && startMonth > selectedMonth) return false;

      return true;
    });
  }, [expenses, selectedYear, selectedMonth]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getStatusBadge = (status) => {
    const badges = {
      PENDING: { color: 'bg-yellow-100 text-yellow-800', label: 'Pendente' },
      PENDING_APPROVAL: { color: 'bg-orange-100 text-orange-800', label: 'Pendente Aprovação' },
      APPROVED: { color: 'bg-blue-100 text-blue-800', label: 'Aprovado' },
      PAID: { color: 'bg-green-100 text-green-800', label: 'Pago' },
      CANCELLED: { color: 'bg-red-100 text-red-800', label: 'Cancelado' },
    };
    const badge = badges[status] || badges.PENDING;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  const totalPending = useMemo(() => {
    return filteredExpenses
      .filter(e => e.status !== 'PAID' && e.status !== 'CANCELLED')
      .reduce((sum, e) => {
        if (e.payment_method === 'INSTALLMENTS') {
          const paid = parseInt(e.installments_paid) || 0;
          const total = parseInt(e.installments_count) || 1;
          const installmentValue = parseFloat(e.amount) / total;
          return sum + (total - paid) * installmentValue;
        }
        return sum + (parseFloat(e.amount) || 0);
      }, 0);
  }, [filteredExpenses]);

  const totalPaid = useMemo(() => {
    return filteredExpenses
      .filter(e => e.status !== 'CANCELLED')
      .reduce((sum, e) => {
        if (e.payment_method === 'INSTALLMENTS') {
          const paid = parseInt(e.installments_paid) || 0;
          const total = parseInt(e.installments_count) || 1;
          const installmentValue = parseFloat(e.amount) / total;
          return sum + paid * installmentValue;
        }
        if (e.status === 'PAID') {
          return sum + (parseFloat(e.amount) || 0);
        }
        return sum;
      }, 0);
  }, [filteredExpenses]);

  const installmentExpenses = filteredExpenses.filter(e => e.payment_method === 'INSTALLMENTS');
  const singleExpenses = filteredExpenses.filter(e => e.payment_method === 'SINGLE');

  const getInstallmentInfo = (expense) => {
    if (expense.payment_method !== 'INSTALLMENTS') return null;
    const total = expense.installments_count || 0;
    const paid = expense.installments_paid || 0;
    const remaining = total - paid;
    const installmentValue = parseFloat(expense.amount) / total;
    const totalPaidAmount = paid * installmentValue;
    const totalRemainingAmount = remaining * installmentValue;
    return { total, paid, remaining, currentInstallment: paid + 1, installmentValue, totalPaidAmount, totalRemainingAmount };
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <section className="bg-gray-50 rounded-lg border border-gray-200 p-3 md:p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-yellow-100 flex items-center justify-center">
              <CreditCard className="w-3.5 h-3.5 md:w-4 md:h-4 text-yellow-600" />
            </div>
            <p className="text-xs md:text-sm text-gray-600">Total pendente</p>
          </div>
          <p className="text-lg md:text-2xl lg:text-3xl font-bold text-yellow-600 truncate">{formatCurrency(totalPending)}</p>
          <p className="text-xs text-gray-500 mt-1">{filteredExpenses.filter(e => e.status !== 'PAID' && e.status !== 'CANCELLED').length} registro(s)</p>
        </section>

        <section className="bg-gray-50 rounded-lg border border-gray-200 p-3 md:p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-3.5 h-3.5 md:w-4 md:h-4 text-green-600" />
            </div>
            <p className="text-xs md:text-sm text-gray-600">Total pago</p>
          </div>
          <p className="text-lg md:text-2xl lg:text-3xl font-bold text-green-600 truncate">{formatCurrency(totalPaid)}</p>
          <p className="text-xs text-gray-500 mt-1">{filteredExpenses.filter(e => e.status === 'PAID').length} registro(s)</p>
        </section>
      </div>

      {installmentExpenses.length > 0 && (
        <section className="bg-gray-50 rounded-lg border border-gray-200 p-3 md:p-4">
          <p className="text-sm font-medium text-gray-600 mb-3">Empréstimos parcelados</p>
          <div className="space-y-3">
            {installmentExpenses.map((expense) => {
              const installmentInfo = getInstallmentInfo(expense);
              return (
                <div key={expense.id} className="p-3 bg-white rounded-lg border border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                        <CreditCard className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{expense.expense_type_name || 'Empréstimo'}</p>
                        <p className="text-xs text-gray-500 truncate">{expense.description || 'Sem descrição'}</p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm md:text-base font-bold text-gray-900">{formatCurrency(expense.amount)}</p>
                      {getStatusBadge(expense.status)}
                    </div>
                  </div>
                  {installmentInfo && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
                        <div className="text-center p-1.5 md:p-2 bg-blue-50 rounded-lg">
                          <p className="text-[10px] md:text-xs text-blue-600 font-medium">Parcela</p>
                          <p className="text-sm md:text-base font-bold text-blue-700">{formatCurrency(installmentInfo.installmentValue)}</p>
                        </div>
                        <div className="text-center p-1.5 md:p-2 bg-green-50 rounded-lg">
                          <p className="text-[10px] md:text-xs text-green-600 font-medium">Atual</p>
                          <p className="text-sm md:text-base font-bold text-green-700">{installmentInfo.currentInstallment}/{installmentInfo.total}</p>
                        </div>
                        <div className="text-center p-1.5 md:p-2 bg-green-50 rounded-lg">
                          <p className="text-[10px] md:text-xs text-green-600 font-medium">Pago</p>
                          <p className="text-sm md:text-base font-bold text-green-700 truncate">{formatCurrency(installmentInfo.totalPaidAmount)}</p>
                        </div>
                        <div className="text-center p-1.5 md:p-2 bg-yellow-50 rounded-lg">
                          <p className="text-[10px] md:text-xs text-yellow-600 font-medium">Restante</p>
                          <p className="text-sm md:text-base font-bold text-yellow-700 truncate">{formatCurrency(installmentInfo.totalRemainingAmount)}</p>
                        </div>
                      </div>
                      <div className="mt-2 md:mt-3">
                        <div className="flex items-center justify-between text-[10px] md:text-xs text-gray-500 mb-1">
                          <span>{installmentInfo.paid} parcelas pagas</span>
                          <span>{Math.round((installmentInfo.paid / installmentInfo.total) * 100)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5 md:h-2">
                          <div
                            className="bg-blue-500 h-1.5 md:h-2 rounded-full transition-all"
                            style={{ width: `${(installmentInfo.paid / installmentInfo.total) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {singleExpenses.length > 0 && (
        <section className="bg-gray-50 rounded-lg border border-gray-200 p-3 md:p-4">
          <p className="text-sm font-medium text-gray-600 mb-3">Vales à vista</p>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {singleExpenses.map((expense) => (
              <div key={expense.id} className="flex items-center justify-between p-2 md:p-3 bg-white rounded-lg">
                <div className="flex items-center gap-2 md:gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-orange-100 flex-shrink-0">
                    <DollarSign className="w-4 h-4 text-orange-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs md:text-sm font-medium text-gray-900 truncate">{expense.expense_type_name || 'Vale'}</p>
                    <p className="text-[10px] md:text-xs text-gray-500 truncate">{expense.description || 'Sem descrição'}</p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-2">
                  <p className="text-sm md:text-base font-bold text-gray-900">{formatCurrency(expense.amount)}</p>
                  {getStatusBadge(expense.status)}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {filteredExpenses.length === 0 && !isLoading && (
        <section className="bg-gray-50 rounded-lg border border-gray-200 p-6 text-center">
          <CreditCard className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm font-medium text-gray-900 mb-1">Nenhum vale/empréstimo registrado</p>
          <p className="text-xs text-gray-500">Você não tem vales ou empréstimos cadastrados.</p>
        </section>
      )}

      {isLoading && (
        <section className="bg-gray-50 rounded-lg border border-gray-200 p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-200 border-t-blue-600 mx-auto mb-2"></div>
          <p className="text-sm text-gray-500">Carregando...</p>
        </section>
      )}
    </div>
  );
};

export default MyTimeRecords;
